import React, { useEffect, useState } from 'react';
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js';
import { PieChart, Pie, Cell } from 'recharts';
import {
  ChartColorLegend,
  ChartLegendBogItem,
  ChartLegendGridItem,
  ChartNoDataGridItem,
  DashboardChartWrapper,
  ResponsiveContainerStyled,
} from 'pages/DashboardPage/PieCharts/styles';
import { Box, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { colors } from 'theme/colors';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { useNavigate } from 'react-router-dom';
import { ERouteLinks } from 'models/route';
import { getLegalEntities } from 'requests/dashboard';
import { useTranslations } from 'hooks/useTranslations';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { EDashboardSortType } from '../../../models/dashboard';

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

interface ILegalEntitiesChart {
  sortType: EDashboardSortType;
  setLoader: React.Dispatch<React.SetStateAction<boolean>>;
}

const LegalEntitiesChart = ({ sortType, setLoader }: ILegalEntitiesChart) => {
  const navigate = useNavigate();
  const [legalEntitiesData, setLegalEntitiesData] = useState<[number, number]>();
  const [totalValue, setTotalValue] = useState(1);
  const [chartData, setChartData] = useState<any>(undefined);
  const { t } = useTranslations();
  const { isDesktop, isMobile } = useThemeBreakpoint();
  const [noData, setNoData] = useState(false);
  const [noDataText, setNoDataText] = useState<string>('');

  useEffect(() => {
    (async () => {
      setLoader(true);
      const data = await getLegalEntities(sortType);
      if (data?.chartValues) {
        const entities: [number, number] = [
          data.chartValues?.[0]?.value,
          data.chartValues?.[1]?.value,
        ];
        setNoData(entities.every((item) => item === 0));
        setLegalEntitiesData(entities);
        setTotalValue(entities.reduce((total, current) => total + current, 0));
      } else {
        setNoData(true);
      }
      setLoader(false);
    })();
  }, [sortType]);

  useEffect(() => {
    switch (sortType) {
      case EDashboardSortType.Day:
        setNoDataText('dashboard.chart.legal.entities.noDataToday');
        break;
      case EDashboardSortType.Week:
        setNoDataText('dashboard.chart.legal.entities.noData7days');
        break;
      case EDashboardSortType.Month:
        setNoDataText('dashboard.chart.legal.entities.noData30days');
        break;
      case EDashboardSortType.Year:
        setNoDataText('dashboard.chart.legal.entities.noDataYear');
        break;
      case EDashboardSortType.All:
      default:
        setNoDataText('dashboard.chart.legal.entities.noDataAll');
        break;
    }
  }, [sortType]);

  useEffect(() => {
    setChartData(undefined);
    if (legalEntitiesData?.length) {
      const newData = [
        {
          id: 'success',
          label: 'Success',
          value: Number(((legalEntitiesData[0] / totalValue) * 100).toFixed(1)),
          color: 'rgba(0, 50, 85, 1)',
        },
        {
          id: 'primary',
          label: 'Primary',
          value: Number(((legalEntitiesData[1] / totalValue) * 100).toFixed(1)),
          color: 'rgba(19, 116, 172, 1)',
        },
      ];
      setTimeout(() => setChartData(newData), 10);
    }
  }, [legalEntitiesData, totalValue]);

  const pieKey = React.useMemo(
    () => `legal-entities-chart-${legalEntitiesData?.join('-')}-${sortType}`,
    [legalEntitiesData, sortType],
  );

  const chartRender = (
    <Grid container sx={{ height: isDesktop ? 'fit-content' : 'auto', flexDirection: !isMobile ? 'row' : 'column-reverse', justifyContent: "center" }}>
      <Grid size={{ xs: 12, sm: 5, md: 5, lg: 12, xl: 5 }} sx={{ padding: '10px 10px', alignItems: "center", alignContent: "center", justifyContent: "center" }}>
        <ChartLegendGridItem
          container
          size={{ xs: 12 }}
          sx={{ ...(!isMobile ? {} : { justifyContent: 'center' }) }}
        >
          <ChartLegendBogItem>
            <ChartColorLegend color={colors.primary900} />
            <Typography variant="body2">
              {t('dashboard.chart.legal.entities.organizations')}
              <Typography variant="subtitle2" sx={{ display: 'inline', paddingLeft: 1.5 }}>
                {legalEntitiesData?.[0]}
              </Typography>
            </Typography>
          </ChartLegendBogItem>
        </ChartLegendGridItem>

        <ChartLegendGridItem
          container
          size={{ xs: 12 }}
          sx={{ ...(!isMobile ? {} : { justifyContent: 'center' }) }}
        >
          <ChartLegendBogItem>
            <ChartColorLegend color={colors.primary400} />
            <Typography variant="body2">
              {t('dashboard.chart.legal.entities.individual')}
              <Typography variant="subtitle2" sx={{ display: 'inline', paddingLeft: 1.5 }}>
                {legalEntitiesData?.[1]}
              </Typography>
            </Typography>
          </ChartLegendBogItem>
        </ChartLegendGridItem>

        <ChartLegendGridItem
          size={{ xs: 12 }}
          sx={{ ...(!isMobile ? {} : { justifyContent: 'center', textAlign: 'center' }) }}
        >
          <Typography variant="body2">
            {t('dashboard.chart.legal.entities.total').toUpperCase()}
            <Typography
              variant="subtitle2"
              component="p"
              sx={{ display: 'inline', paddingLeft: 1.5 }}
            >
              {totalValue}
            </Typography>
          </Typography>
        </ChartLegendGridItem>
      </Grid>
      <Grid
        size={{ xs: 12, sm: 5, md: 5, lg: 12, xl: 5 }}
        sx={{
          ...(!isMobile
            ? { display: 'block', alignSelf: 'center' }
            : { display: 'flex', justifyContent: 'center' }),
        }}
      >
        {chartData && (
          <ResponsiveContainerStyled width="100%" height={isDesktop ? 220 : 200}>
            <PieChart>
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="label"
                labelLine={false}
                animationDuration={900}
                label={({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
                  const radius = innerRadius + (outerRadius - innerRadius) / 2;
                  const RADIAN = Math.PI / 180;
                  const x = cx + radius * Math.cos(-(midAngle || 1) * RADIAN);
                  const y = cy + radius * Math.sin(-(midAngle || 1) * RADIAN);

                  return (
                    <text
                      x={x}
                      y={y}
                      fill="#fff"
                      textAnchor="middle"
                      dominantBaseline="central"
                      fontSize={14}
                      fontWeight={600}
                    >
                      {`${chartData[index].value}%`}
                    </text>
                  );
                }}
              >
                {chartData.map((entry: any) => (
                  <Cell key={entry.id} fill={entry.color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainerStyled>
        )}
      </Grid>
    </Grid>
  );

  const noDataRender = (
    <ChartNoDataGridItem container size={{ xs: 12 }}>
      <Typography variant="body2">{t(noDataText)}</Typography>
    </ChartNoDataGridItem>
  );

  return (
    <DashboardChartWrapper>
      <Typography variant="subtitle1" sx={{ textAlign: 'center', marginBottom: 2 }}>
        {t('dashboard.chart.legal.entities.label')}
      </Typography>

      {noData ? noDataRender : chartRender}
      <Box
        sx={{
          ...(!isMobile ? { padding: '20px 10px 0px 10px' } : { padding: '5px 10px 0 10px' }),
        }}
      >
        <Button
          variant={EButtonVariants.outlined}
          fullWidth
          label={t('dashboard.chart.legal.entities.seeLegalEntities')}
          onClick={() => navigate(ERouteLinks.LegalEntities)}
          size={EButtonSizes.small}
        />
      </Box>
    </DashboardChartWrapper>
  );
};

export default LegalEntitiesChart;
